"use client";

import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import { useToast } from "@/app/_module/components/ui/use-toast";
import { forumService, ForumEligibility, shellPrefix } from "../components/ForumService";

// Single source of truth for forum access on the CRM surface. The backend
// enforces everything again server-side — this is UX routing only.
export function useForumAccess() {
  const { toast } = useToast();
  const router = useRouter();
  const queryClient = useQueryClient();

  const { data: eligibility, isLoading } = useQuery<ForumEligibility>({
    queryKey: ["forum", "eligibility"],
    queryFn: forumService.getEligibility,
    staleTime: 60_000,
    retry: 1,
  });

  // Gate a write action: runs cb when allowed, otherwise explains why (and
  // routes upgrades to billing). Backend still re-checks on every request.
  const requireWrite = useCallback(
    (cb: () => void) => {
      if (!eligibility) return;
      if (eligibility.canWrite) {
        cb();
        return;
      }
      switch (eligibility.reason) {
        case "FORUM_SUBSCRIPTION_REQUIRED":
          toast({
            title: "Subscription required",
            description: "An active InsuraCentral subscription is required to post in the community.",
            variant: "destructive",
            duration: 6000,
          });
          router.push(shellPrefix() ? "/m/settings/subscription" : "/settings?tab=subscription");
          break;
        case "FORUM_DEMO_READ_ONLY":
          toast({
            title: "Read-only in the demo",
            description: "Demo accounts can read the Community but can't post, reply or vote. Start a free trial to join in.",
            duration: 6000,
          });
          break;
        case "FORUM_BANNED":
          toast({
            title: "You're suspended from the community",
            description: eligibility.banned?.until
              ? `Suspension ends ${new Date(eligibility.banned.until).toLocaleDateString()}.`
              : eligibility.banned?.reason || "Contact support if you think this is a mistake.",
            variant: "destructive",
            duration: 6000,
          });
          break;
        default:
          toast({
            title: "Posting unavailable",
            description: "You can read the community but posting isn't available on your account.",
            variant: "destructive",
            duration: 5000,
          });
      }
    },
    [eligibility, toast, router],
  );

  const refresh = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ["forum", "eligibility"] }),
    [queryClient],
  );

  return {
    eligibility,
    isLoading,
    canSee: !!eligibility?.eligible,
    canWrite: !!eligibility?.canWrite,
    isModerator: !!eligibility?.isModerator,
    viewerId: eligibility?.viewer?.id || null,
    requireWrite,
    refresh,
  };
}
